import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    Self,
} from "@angular/core";
import { ControlValueAccessor, NgControl } from "@angular/forms";
import { LocalStoreService } from "../../services";

@Component({
    selector: "app-drop-number-selector",
    templateUrl: "./drop-number-selector.component.html",
    styleUrls: ["./drop-number-selector.component.scss"],
})
export class DropNumberSelectorComponent implements OnInit, OnDestroy, ControlValueAccessor {

    @Input() value: number = 12;
    @Input() min: number = 1;
    @Input() max: number = 412;
    @Input() label: string = "";
    @Input() presetValues: number[] = [];
    @Input() showManualInput: boolean = false;
    @Input() noManualEdit: boolean = false;
    @Input() step: number = 1;
    @Input() placeholder: string = "";

    // Reactive forms support (validations only - formControl is injected)
    @Input() validations: any[] = [];

    @Output() valueChange: EventEmitter<number> = new EventEmitter<number>();
    @Output() onChange: EventEmitter<number> = new EventEmitter<number>();

    // Reference to the form control (when used with formControlName)
    public control: any = null;

    private onChangeFn: (value: number) => void = () => {
    };
    private onTouchedFn: () => void = () => {
    };
    public disabled = false;

    showDropdown = false;
    showInput = false;
    theme = "light";
    dropdownOpenUpward = false;
    dropdownPosition = { top: "0", left: "0", width: "0" };
    private scrollListeners: Array<{ element: HTMLElement, listener: EventListener }> = [];
    private resizeListener: EventListener | null = null;

    constructor(
        private localStoreService: LocalStoreService,
        @Optional() @Self() public ngControl: NgControl,
        private elementRef: ElementRef,
    ) {
        // Set the value accessor on ngControl if it exists (for reactive forms)
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit() {
        try {
            // Initialize theme
            this.theme = this.localStoreService.getStorageValConfig("theme") || "light";
        } catch (error) {
            console.warn("DropNumberSelector: Failed to load theme, using default", error);
            this.theme = "light";
        }

        try {
            // Set preset values
            if (this.presetValues.length === 0) {
                // Use dynamic generation if min/max/step look intentional (not defaults)
                // and the range is reasonable (< 200 values)
                const isCustomRange = (this.min !== 1 || this.max !== 412 || this.step !== 1);
                const valueCount = Math.ceil((this.max - this.min) / this.step) + 1;
                const isReasonableRange = valueCount <= 200;

                if (isCustomRange && isReasonableRange && this.step > 0) {
                    this.presetValues = this.generatePresetValues(this.min, this.max, this.step);
                } else {
                    // Use default preset values: smart range from 1-150
                    this.presetValues = Array.from({ length: 150 }, (_, i) => i + 1);
                }
            }
        } catch (error) {
            console.warn("DropNumberSelector: Failed to generate preset values, using defaults", error);
            this.presetValues = Array.from({ length: 150 }, (_, i) => i + 1);
        }

        try {
            // Get reference to the form control if using reactive forms
            if (this.ngControl && this.ngControl.control) {
                this.control = this.ngControl.control;
            }
        } catch (error) {
            console.warn("DropNumberSelector: Failed to get form control reference", error);
        }

        // Debug logging
        try {
            console.log("DropNumberSelector Debug:", {
                label: this.label,
                showManualInput: this.showManualInput,
                value: this.value,
                min: this.min,
                max: this.max,
                step: this.step,
                theme: this.theme,
                isDarkTheme: this.isDarkTheme,
                hasControl: !!this.control,
                presetValues: this.presetValues,
            });
        } catch (error) {
            // Silent fail for debug logging
        }
    }

    private generatePresetValues(min: number, max: number, step: number): number[] {
        try {
            const values: number[] = [];
            for (let i = min; i <= max; i += step) {
                // Round to avoid floating point precision issues
                const roundedValue = Math.round(i * 1000) / 1000;
                values.push(roundedValue);
            }
            return values;
        } catch (error) {
            console.warn("DropNumberSelector: Error in generatePresetValues, returning default range", error);
            return Array.from({ length: 150 }, (_, i) => i + 1);
        }
    }

    @HostListener("document:click", ["$event"])
    onDocumentClick(event: any) {
        try {
            if (!event.target.closest(".drop-number-selector")) {
                this.closeDropdown();
            }
        } catch (error) {
            console.warn("DropNumberSelector: Error in document click handler", error);
        }
    }

    @HostListener("document:keydown", ["$event"])
    onKeyDown(event: KeyboardEvent) {
        try {
            // Only handle keyboard events when dropdown is open
            if (!this.showDropdown) return;

            switch (event.key) {
                case "ArrowDown":
                    event.preventDefault();
                    this.navigateNext();
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    this.navigatePrevious();
                    break;
                case "PageDown":
                    event.preventDefault();
                    this.jumpToHighest();
                    break;
                case "PageUp":
                    event.preventDefault();
                    this.jumpToLowest();
                    break;
                case "Enter":
                    event.preventDefault();
                    this.closeDropdown();
                    break;
                case "Escape":
                    event.preventDefault();
                    this.closeDropdown();
                    break;
            }
        } catch (error) {
            console.warn("DropNumberSelector: Error in keyboard handler", error);
        }
    }

    private navigateNext() {
        try {
            const currentValue = Number(this.value) || this.min;
            const currentIndex = this.presetValues.findIndex(v => v === currentValue);

            if (currentIndex === -1) {
                // Current value not in presets, find next higher value
                const nextValue = this.presetValues.find(v => v > currentValue);
                if (nextValue !== undefined) {
                    this.updateValue(nextValue);
                    this.scrollToValue(nextValue);
                }
            } else if (currentIndex < this.presetValues.length - 1) {
                // Move to next preset value
                this.updateValue(this.presetValues[currentIndex + 1]);
                this.scrollToValue(this.presetValues[currentIndex + 1]);
            }
        } catch (error) {
            console.warn("DropNumberSelector: Error in navigateNext", error);
        }
    }

    private navigatePrevious() {
        try {
            const currentValue = Number(this.value) || this.min;
            const currentIndex = this.presetValues.findIndex(v => v === currentValue);

            if (currentIndex === -1) {
                // Current value not in presets, find next lower value
                const prevValue = [...this.presetValues].reverse().find(v => v < currentValue);
                if (prevValue !== undefined) {
                    this.updateValue(prevValue);
                    this.scrollToValue(prevValue);
                }
            } else if (currentIndex > 0) {
                // Move to previous preset value
                this.updateValue(this.presetValues[currentIndex - 1]);
                this.scrollToValue(this.presetValues[currentIndex - 1]);
            }
        } catch (error) {
            console.warn("DropNumberSelector: Error in navigatePrevious", error);
        }
    }

    private jumpToLowest() {
        try {
            if (this.presetValues.length > 0) {
                this.updateValue(this.presetValues[0]);
                this.scrollToValue(this.presetValues[0]);
            }
        } catch (error) {
            console.warn("DropNumberSelector: Error in jumpToLowest", error);
        }
    }

    private jumpToHighest() {
        try {
            if (this.presetValues.length > 0) {
                this.updateValue(this.presetValues[this.presetValues.length - 1]);
                this.scrollToValue(this.presetValues[this.presetValues.length - 1]);
            }
        } catch (error) {
            console.warn("DropNumberSelector: Error in jumpToHighest", error);
        }
    }

    private scrollToValue(value: number) {
        try {
            // Wait for DOM update
            setTimeout(() => {
                try {
                    const dropdown = this.elementRef.nativeElement.querySelector(".preset-list");
                    if (!dropdown) return;

                    // Find the selected option element
                    const selectedOption = dropdown.querySelector(".preset-option.selected");
                    if (!selectedOption) return;

                    // Scroll the selected option into view
                    selectedOption.scrollIntoView({
                        behavior: "smooth",
                        block: "nearest",
                        inline: "nearest",
                    });
                } catch (innerError) {
                    console.warn("DropNumberSelector: Error scrolling to value", innerError);
                }
            }, 0);
        } catch (error) {
            console.warn("DropNumberSelector: Error in scrollToValue", error);
        }
    }

    increment() {
        try {
            if (this.disabled) return;

            // CRITICAL: Ensure both value and step are numbers before addition
            const currentValue = Number(this.value) || 0;
            const stepValue = Number(this.step) || 1;
            const newValue = currentValue + stepValue;
            const maxValue = Number(this.max);
            const roundedValue = this.roundToStep(Math.min(maxValue, newValue));

            this.updateValue(roundedValue);
        } catch (error) {
            console.warn("DropNumberSelector: Error in increment", error);
        }
    }

    decrement() {
        try {
            if (this.disabled) return;

            // CRITICAL: Ensure both value and step are numbers before subtraction
            const currentValue = Number(this.value) || 0;
            const stepValue = Number(this.step) || 1;
            const newValue = currentValue - stepValue;
            const minValue = Number(this.min);
            const roundedValue = this.roundToStep(Math.max(minValue, newValue));

            this.updateValue(roundedValue);
        } catch (error) {
            console.warn("DropNumberSelector: Error in decrement", error);
        }
    }

    private roundToStep(value: number): number {
        try {
            // Determine decimal places from step
            const stepStr = this.step.toString();
            const decimalPlaces = stepStr.includes(".") ? stepStr.split(".")[1].length : 0;
            // Round to avoid floating point precision issues
            return Number(value.toFixed(decimalPlaces));
        } catch (error) {
            console.warn("DropNumberSelector: Error in roundToStep, returning raw value", error);
            return value;
        }
    }

    openDropdown() {
        try {
            // Get the input element's position
            const element = this.elementRef.nativeElement.querySelector(".canva-number-input");
            if (!element) {
                console.warn("DropNumberSelector: Input element not found");
                return;
            }

            const rect = element.getBoundingClientRect();
            const dropdownHeight = 250; // max-height of dropdown

            // Check viewport space
            const viewportSpaceBelow = window.innerHeight - rect.bottom;
            const viewportSpaceAbove = rect.top;

            // Determine if dropdown should open upward
            this.dropdownOpenUpward = viewportSpaceBelow < 150 || (viewportSpaceBelow < dropdownHeight && viewportSpaceAbove > viewportSpaceBelow);

            // Calculate fixed position for dropdown
            this.dropdownPosition = {
                left: `${rect.left}px`,
                width: `${rect.width}px`,
                top: this.dropdownOpenUpward ? "auto" : `${rect.bottom + 4}px`,
            };

            // If opening upward, set bottom position
            if (this.dropdownOpenUpward) {
                this.dropdownPosition["bottom"] = `${window.innerHeight - rect.top + 4}px`;
            }

            this.showDropdown = true;

            // Attach scroll listeners to all parent containers
            this.attachScrollListeners();

            // Attach resize listener to close dropdown on browser resize
            this.attachResizeListener();

            // Scroll to current value or nearest value after dropdown opens
            this.scrollToCurrentOrNearestValue();

            // Debug logging for development
            try {
                console.log("DropNumberSelector - Dropdown Direction:", {
                    dropdownOpenUpward: this.dropdownOpenUpward,
                    viewportSpaceBelow,
                    viewportSpaceAbove,
                    dropdownPosition: this.dropdownPosition,
                    label: this.label,
                });
            } catch (logError) {
                // Silent fail for debug logging
            }
        } catch (error) {
            console.warn("DropNumberSelector: Error opening dropdown", error);
            this.showDropdown = false; // Ensure dropdown state is clean
        }
    }

    private scrollToCurrentOrNearestValue() {
        try {
            setTimeout(() => {
                try {
                    const dropdown = this.elementRef.nativeElement.querySelector(".preset-list");
                    if (!dropdown) return;

                    const currentValue = Number(this.value) || this.min;
                    const currentIndex = this.presetValues.findIndex(v => v === currentValue);

                    let targetOption: Element | null = null;

                    if (currentIndex !== -1) {
                        // Current value exists in preset list - scroll to it
                        targetOption = dropdown.querySelector(".preset-option.selected");
                    } else {
                        // Current value not in presets - find nearest value
                        let nearestValue = this.presetValues[0];
                        let minDiff = Math.abs(currentValue - nearestValue);

                        for (const preset of this.presetValues) {
                            const diff = Math.abs(currentValue - preset);
                            if (diff < minDiff) {
                                minDiff = diff;
                                nearestValue = preset;
                            }
                        }

                        // Find the option element for nearest value
                        const options = dropdown.querySelectorAll(".preset-option");
                        const nearestIndex = this.presetValues.indexOf(nearestValue);
                        if (nearestIndex !== -1 && options[nearestIndex]) {
                            targetOption = options[nearestIndex];
                        }
                    }

                    // Scroll to target option
                    if (targetOption) {
                        targetOption.scrollIntoView({
                            behavior: "auto", // Use 'auto' instead of 'smooth' for initial scroll
                            block: "center",
                            inline: "nearest",
                        });
                    }
                } catch (innerError) {
                    console.warn("DropNumberSelector: Error in scroll timeout", innerError);
                }
            }, 50); // Small delay to ensure dropdown is fully rendered
        } catch (error) {
            console.warn("DropNumberSelector: Error in scrollToCurrentOrNearestValue", error);
        }
    }

    private hasOverflowParent(element: HTMLElement): boolean {
        try {
            if (!element) {
                return false;
            }

            let parent = element.parentElement;

            while (parent && parent !== document.body) {
                const style = window.getComputedStyle(parent);
                const overflowY = style.overflowY;

                // Check if parent has overflow that could clip content
                if (overflowY === "hidden" || overflowY === "auto" || overflowY === "scroll") {
                    return true;
                }

                parent = parent.parentElement;
            }

            return false;
        } catch (error) {
            console.warn("DropNumberSelector: Error in hasOverflowParent", error);
            return false;
        }
    }

    private getScrollParent(element: HTMLElement): HTMLElement | null {
        try {
            if (!element) {
                return null;
            }

            let parent = element.parentElement;

            while (parent) {
                const overflowY = window.getComputedStyle(parent).overflowY;
                const isScrollable = overflowY === "auto" || overflowY === "scroll" || overflowY === "hidden";

                // Return first parent with overflow constraints
                if (isScrollable) {
                    return parent;
                }

                parent = parent.parentElement;
            }

            return document.body;
        } catch (error) {
            console.warn("DropNumberSelector: Error in getScrollParent", error);
            return document.body;
        }
    }

    closeDropdown() {
        try {
            this.showDropdown = false;
            this.dropdownOpenUpward = false;
            this.removeScrollListeners();
            this.removeResizeListener();
        } catch (error) {
            console.warn("DropNumberSelector: Error in closeDropdown", error);
            // Ensure dropdown state is clean even on error
            this.showDropdown = false;
            this.dropdownOpenUpward = false;
        }
    }

    private attachScrollListeners() {
        try {
            // Remove any existing listeners first
            this.removeScrollListeners();

            // Find all scrollable parent containers and attach scroll listeners
            let parent = this.elementRef.nativeElement.parentElement;

            const scrollListener = () => {
                this.closeDropdown();
            };

            while (parent) {
                const style = window.getComputedStyle(parent);
                const overflowY = style.overflowY;

                // Attach listener to any scrollable parent
                if (overflowY === "auto" || overflowY === "scroll" || overflowY === "hidden") {
                    parent.addEventListener("scroll", scrollListener, { passive: true });
                    this.scrollListeners.push({ element: parent, listener: scrollListener });
                }

                parent = parent.parentElement;
            }

            // Also listen to window scroll
            window.addEventListener("scroll", scrollListener, { passive: true });
            this.scrollListeners.push({ element: window as any, listener: scrollListener });
        } catch (error) {
            console.warn("DropNumberSelector: Error attaching scroll listeners", error);
        }
    }

    private removeScrollListeners() {
        try {
            this.scrollListeners.forEach(({ element, listener }) => {
                element.removeEventListener("scroll", listener);
            });
            this.scrollListeners = [];
        } catch (error) {
            console.warn("DropNumberSelector: Error removing scroll listeners", error);
            this.scrollListeners = [];
        }
    }

    private attachResizeListener() {
        try {
            // Remove any existing listener first
            this.removeResizeListener();

            // Create resize listener that closes dropdown
            this.resizeListener = () => {
                this.closeDropdown();
            };

            // Attach listener to window
            window.addEventListener("resize", this.resizeListener, { passive: true });
        } catch (error) {
            console.warn("DropNumberSelector: Error attaching resize listener", error);
        }
    }

    private removeResizeListener() {
        try {
            if (this.resizeListener) {
                window.removeEventListener("resize", this.resizeListener);
                this.resizeListener = null;
            }
        } catch (error) {
            console.warn("DropNumberSelector: Error removing resize listener", error);
            this.resizeListener = null;
        }
    }

    toggleDropdown() {
        try {
            this.showDropdown = !this.showDropdown;
        } catch (error) {
            console.warn("DropNumberSelector: Error in toggleDropdown", error);
        }
    }

    selectPresetValue(preset: number) {
        try {
            this.updateValue(preset);
            this.closeDropdown();
        } catch (error) {
            console.warn("DropNumberSelector: Error in selectPresetValue", error);
        }
    }

    toggleManualInput() {
        try {
            this.showInput = !this.showInput;

            // Auto-focus the input field when showing it
            if (this.showInput) {
                setTimeout(() => {
                    try {
                        const inputField = this.elementRef.nativeElement.querySelector(".manual-input-field");
                        if (inputField) {
                            inputField.focus();
                            // Select all text for easy editing
                            inputField.select();
                        }
                    } catch (innerError) {
                        console.warn("DropNumberSelector: Error focusing input field", innerError);
                    }
                }, 0);
            }
        } catch (error) {
            console.warn("DropNumberSelector: Error in toggleManualInput", error);
        }
    }

    onInputChange(event: any) {
        try {
            const value = parseInt(event.target.value, 10);
            if (!isNaN(value) && value >= this.min && value <= this.max) {
                this.updateValue(value);
            }
        } catch (error) {
            console.warn("DropNumberSelector: Error in onInputChange", error);
        }
    }

    onInputBlur(event: any) {
        try {
            const value = parseInt(event.target.value, 10);
            if (isNaN(value) || value < this.min) {
                this.updateValue(this.min);
            } else if (value > this.max) {
                this.updateValue(this.max);
            } else {
                this.updateValue(value);
            }
        } catch (error) {
            console.warn("DropNumberSelector: Error in onInputBlur", error);
        }
    }

    onInputEnter(event: any) {
        try {
            event.target.blur();
            this.closeDropdown();
        } catch (error) {
            console.warn("DropNumberSelector: Error in onInputEnter", error);
        }
    }

    private updateValue(newValue: number) {
        try {
            // CRITICAL: Ensure newValue is a number, not a string
            const numericValue = Number(newValue);
            const currentValue = Number(this.value) || 0;

            if (numericValue !== currentValue) {
                // CRITICAL: Always store as number to prevent string concatenation
                this.value = numericValue;
                // Emit for legacy two-way binding
                this.valueChange.emit(numericValue);
                this.onChange.emit(numericValue);
                // Notify reactive forms
                this.onChangeFn(numericValue);
                this.onTouchedFn();
            }
        } catch (error) {
            console.warn("DropNumberSelector: Error in updateValue", error);
        }
    }

    get isDarkTheme(): boolean {
        try {
            return this.theme === "dark" || this.theme === "app-theme-dark";
        } catch (error) {
            console.warn("DropNumberSelector: Error in isDarkTheme getter", error);
            return false;
        }
    }

    // ControlValueAccessor implementation
    writeValue(value: number): void {
        try {
            if (value !== undefined && value !== null) {
                // CRITICAL: Ensure value is stored as a number, not a string
                // This prevents string concatenation in increment/decrement
                this.value = Number(value);
            }
        } catch (error) {
            console.warn("DropNumberSelector: Error in writeValue", error);
        }
    }

    registerOnChange(fn: any): void {
        try {
            this.onChangeFn = fn;
        } catch (error) {
            console.warn("DropNumberSelector: Error in registerOnChange", error);
        }
    }

    registerOnTouched(fn: any): void {
        try {
            this.onTouchedFn = fn;
        } catch (error) {
            console.warn("DropNumberSelector: Error in registerOnTouched", error);
        }
    }

    setDisabledState(isDisabled: boolean): void {
        try {
            this.disabled = isDisabled;
        } catch (error) {
            console.warn("DropNumberSelector: Error in setDisabledState", error);
        }
    }

    // Helper to get form control for validation
    get formControl() {
        try {
            return this.control;
        } catch (error) {
            console.warn("DropNumberSelector: Error getting formControl", error);
            return null;
        }
    }

    ngOnDestroy() {
        try {
            // Clean up scroll listeners when component is destroyed
            this.removeScrollListeners();
            this.removeResizeListener();
        } catch (error) {
            console.warn("DropNumberSelector: Error in ngOnDestroy", error);
        }
    }
}
